import React, { useEffect, useState } from "react";
import { useCommonContext } from "contexts/CommonContext";
import {
  RankingRole,
  RankingSortOrder,
  useGetGlobalCounterQuery,
  useGetMaterialRankingQuery,
} from "reducers/rankingApi";
import GlobalCounterSection from "components/globalRanking/GlobalCounterSection";
import RankingFilters, {
  RankingMaterialFilter,
} from "components/globalRanking/RankingFilters";
import RankingPagination from "components/globalRanking/RankingPagination";
import RankingTable from "components/globalRanking/RankingTable";
import RankingMyRankBar from "components/globalRanking/RankingMyRankBar";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { UserTypeEnum } from "common/enums/UserType.enum";

const PAGE_SIZE = 5;

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="flex w-full items-stretch gap-[14px] border-b border-[#d5d5d5] py-[10px]">
    <div className="w-[4px] rounded-[2px] bg-primary" />
    <h2 className="text-[18px] font-bold leading-[160%] text-primary md:text-[22px]">
      {children}
    </h2>
  </div>
);

const GlobalRanking = () => {
  const { setHeaderTitle } = useCommonContext();
  const { userData } = useSelector((state: RootState) => state.auth);
  const [rankingRole, setRankingRole] = useState<RankingRole>("seller");
  const [material, setMaterial] = useState("all");
  const [sortOrder, setSortOrder] = useState<RankingSortOrder>("desc");
  const [page, setPage] = useState(1);

  const { data: counterResponse } = useGetGlobalCounterQuery();
  const {
    data: rankingResponse,
    isLoading,
    isError,
  } = useGetMaterialRankingQuery({
    role: rankingRole,
    page,
    limit: PAGE_SIZE,
    sort_order: sortOrder,
    ...(material === "all" ? {} : { category_id: Number(material) }),
  });

  const counter = counterResponse?.data ?? null;
  const rankingRows = rankingResponse?.data.items ?? [];
  const myRank = rankingResponse?.data.my_rank ?? null;
  const totalPages = Math.max(1, rankingResponse?.data.meta.total_pages ?? 1);
  const errorMessage = isError ? "ランキングデータの取得に失敗しました" : "";

  useEffect(() => {
    setHeaderTitle("全体ランキング / グローバルカウンター");
  }, [setHeaderTitle]);

  const handleRoleChange = (role: RankingRole) => {
    setRankingRole(role);
    setPage(1);
  };

  const handleMaterialChange = (value: string) => {
    setMaterial(value);
    setPage(1);
  };

  const handleSortOrderChange = (value: RankingSortOrder) => {
    setSortOrder(value);
    setPage(1);
  };

  const currentUserRankingRole: RankingRole | null =
    userData?.type === UserTypeEnum.COMPANY_SELLER
      ? "seller"
      : userData?.type === UserTypeEnum.COMPANY_BUYER
        ? "buyer"
        : null;
  const shouldShowMyRank =
    myRank !== null && currentUserRankingRole === rankingRole;

  return (
    <main className="flex min-h-[752px] w-full flex-col gap-[30px] overflow-hidden bg-white p-[20px] pb-[100px] md:min-h-[924px] md:gap-[40px] md:p-[40px]">
      <section className="flex w-full flex-col gap-[30px]">
        <SectionTitle>POLAR ISLAND メンバー全員の総合成果</SectionTitle>
        <GlobalCounterSection counter={counter} />
      </section>

      <section className="flex w-full flex-col gap-[24px]">
        <SectionTitle>グローバル・リーダーボード</SectionTitle>

        <RankingFilters
          rankingRole={rankingRole}
          onRoleChange={handleRoleChange}
        />

        <div className="overflow-hidden rounded-[6px] border border-[#002959] bg-[linear-gradient(154.13deg,#1B4B86_0%,#002959_99.28%)] p-[10px] shadow-md">
          <RankingMaterialFilter
            material={material}
            sortOrder={sortOrder}
            onMaterialChange={handleMaterialChange}
            onSortOrderChange={handleSortOrderChange}
          />
          <RankingTable
            rows={rankingRows}
            isLoading={isLoading && rankingRows.length === 0}
            errorMessage={errorMessage}
          />
          <RankingPagination
            page={page}
            totalPages={totalPages}
            visible={rankingRows.length > 0 && errorMessage === ""}
            onPageChange={setPage}
          />
          {shouldShowMyRank ? <RankingMyRankBar myRank={myRank} /> : null}
        </div>
      </section>
    </main>
  );
};

export default GlobalRanking;
